import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiTrendingUp, FiDollarSign, FiClock, FiUsers, FiFileText } = FiIcons;

const ProblemStatement = () => {
  const problems = [
    {
      icon: FiAlertTriangle,
      title: 'Regulatory Complexity',
      description: 'AML/CFT regulations are intricate and constantly evolving, requiring specialized knowledge that most companies lack internally.',
      impact: 'High'
    },
    {
      icon: FiTrendingUp,
      title: 'Growing Compliance Burden',
      description: 'More non-financial companies are being brought under AML/CFT scope, creating unexpected compliance obligations.',
      impact: 'Critical'
    },
    {
      icon: FiDollarSign,
      title: 'High Cost of Non-Compliance',
      description: 'Regulatory penalties can reach millions, while reputational damage can be irreversible for businesses.',
      impact: 'Severe'
    },
    {
      icon: FiClock,
      title: 'Time-Critical Implementation',
      description: 'Compliance deadlines are strict, leaving little room for learning curves or trial-and-error approaches.',
      impact: 'Urgent'
    },
    {
      icon: FiUsers,
      title: 'Talent Shortage',
      description: 'Qualified MLRO professionals are scarce and expensive, making full-time hiring challenging for most companies.',
      impact: 'High'
    },
    {
      icon: FiFileText,
      title: 'Documentation Complexity',
      description: 'Proper documentation and reporting require specific expertise to avoid regulatory scrutiny and penalties.',
      impact: 'Critical'
    }
  ];

  const impactColors = {
    'High': 'text-amber-600 bg-amber-100',
    'Critical': 'text-red-600 bg-red-100',
    'Severe': 'text-red-700 bg-red-200',
    'Urgent': 'text-orange-600 bg-orange-100'
  };

  return (
    <section id="problem" className="py-24 bg-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-50 rounded-full blur-3xl opacity-50"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
            <span>Critical Business Challenge</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            The AML/CFT Compliance Challenge
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Companies across industries face unprecedented regulatory pressure. Without proper expertise, 
            the risk of non-compliance grows exponentially, threatening operations and reputation.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problems.map((problem, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group bg-white border-2 border-gray-200 rounded-2xl p-6 hover:border-emerald-500 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <SafeIcon icon={problem.icon} className="w-7 h-7 text-white" />
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${impactColors[problem.impact]}`}>
                  {problem.impact}
                </span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">{problem.title}</h3>
              <p className="text-gray-600 leading-relaxed">{problem.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 bg-gradient-to-r from-gray-900 to-emerald-900 rounded-3xl p-10 text-center relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNiIgc3Ryb2tlPSIjMTBiOTgxIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-20"></div>
          <div className="relative">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              The Cost of Inaction is Too High
            </h3>
            <p className="text-emerald-100 text-lg max-w-3xl mx-auto leading-relaxed">
              Regulatory fines, business disruption, and reputational damage can devastate companies. 
              The question isn't whether you can afford professional compliance support – it's whether you can afford not to have it.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemStatement;